import React from "react";
import { FileSignature, Scale, ShieldCheck, Clock, AlertTriangle, BadgePercent } from "lucide-react";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import { useTranslation } from "react-i18next";

const titleCase = (s) => (typeof s === "string" ? s.replace(/_/g, " ") : "—");

export default function ContractEnrichmentPanel({ enrichment }) {
  const { t } = useTranslation("components");
  const e = enrichment || {};
  const currency = (e.currency || DEFAULT_CURRENCY).toUpperCase();
  const money = (n) => fmtMoney(n, currency) ?? "—";
  const ind = e.indemnification;
  const cap = e.liability_cap;
  const ar = e.auto_renewal;
  const term = e.termination;
  const reqIns = e.required_insurance || [];
  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6"
      data-testid="contract-enrichment-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-md grid place-items-center bg-indigo-50 text-indigo-700">
          <FileSignature size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{t("enrichment.contract.section_title")}</h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile label={t("enrichment.contract.subtype")} value={t(`enrichment.contract.subtype_options.${e.contract_subtype}`, titleCase(e.contract_subtype) || "—")} testid="contract-enrich-subtype" />
        <Tile label={t("enrichment.contract.contract_value")} value={money(e.contract_value)} accent="emerald" testid="contract-enrich-value" />
        <Tile label={t("enrichment.contract.payment_terms")} value={e.payment_terms_days == null || isNaN(e.payment_terms_days) ? "—" : t("enrichment.contract.days", { count: Number(e.payment_terms_days) })} testid="contract-enrich-payment-terms" />
        <Tile label={t("enrichment.contract.billing")} value={t(`enrichment.contract.billing_options.${e.billing_frequency}`, titleCase(e.billing_frequency) || "—")} testid="contract-enrich-billing" />
      </div>

      {/* Indemnification & liability cap — the heart of contract risk */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {ind && (
          <div
            className={`px-4 py-3 rounded-lg border ${
              ind.mutual
                ? "bg-emerald-50/40 border-emerald-200"
                : (ind.cap_amount == null && ind.cap_multiple_of_fees == null)
                ? "bg-rose-50/60 border-rose-200"
                : "bg-amber-50/60 border-amber-200"
            }`}
            data-testid="contract-enrich-indemnification"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Scale size={13} className={ind.mutual ? "text-emerald-700" : "text-rose-700"} />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.contract.indemnification")}</p>
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed font-semibold">
              {ind.mutual ? t("enrichment.contract.mutual") : t("enrichment.contract.one_sided")}
              {!ind.mutual && ind.indemnitor ? t("enrichment.contract.indemnifies_by", { indemnitor: ind.indemnitor, indemnitee: ind.indemnitee ? ` ${ind.indemnitee}` : "" }) : ""}
            </p>
            <p className="text-xs text-zinc-600 mt-1">
              {t("enrichment.contract.cap_label")} {ind.cap_amount != null ? money(ind.cap_amount) :
                ind.cap_multiple_of_fees != null ? t("enrichment.contract.multiple_of_fees", { multiple: ind.cap_multiple_of_fees }) :
                <span className="text-rose-700 font-semibold">{t("enrichment.contract.uncapped")}</span>}
            </p>
            {ind.carveouts?.length > 0 && (
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                {t("enrichment.contract.carveouts_label")} {ind.carveouts.join(", ")}
              </p>
            )}
          </div>
        )}
        {cap && (
          <div
            className={`px-4 py-3 rounded-lg border ${
              (cap.amount == null && cap.multiple_of_fees == null)
                ? "bg-rose-50/60 border-rose-200"
                : "bg-zinc-50 border-zinc-200"
            }`}
            data-testid="contract-enrich-liability-cap"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <ShieldCheck size={13} className="text-zinc-700" />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.contract.liability_cap")}</p>
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed font-semibold">
              {cap.amount != null ? money(cap.amount) :
                cap.multiple_of_fees != null ? t("enrichment.contract.multiple_of_fees", { multiple: cap.multiple_of_fees }) :
                <span className="text-rose-700">{t("enrichment.contract.uncapped")}</span>}
            </p>
            {cap.carveouts?.length > 0 && (
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                {t("enrichment.contract.carveouts_label")} {cap.carveouts.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Renewal & termination */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {ar && (
          <div
            className={`px-4 py-3 rounded-lg border ${ar.present ? "bg-amber-50/60 border-amber-200" : "bg-emerald-50/40 border-emerald-200"}`}
            data-testid="contract-enrich-auto-renewal"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Clock size={13} className={ar.present ? "text-amber-700" : "text-emerald-700"} />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.contract.auto_renewal")}</p>
            </div>
            <p className="text-sm font-semibold">
              {ar.present
                ? (ar.term_months != null
                    ? t("enrichment.contract.renews_every", { count: ar.term_months })
                    : t("enrichment.contract.renews_every_unknown"))
                : t("enrichment.contract.does_not_auto_renew")}
            </p>
            {ar.present && ar.notice_window_days != null && (
              <p className="text-xs text-zinc-600 mt-1">{t("enrichment.contract.notice_window", { count: ar.notice_window_days })}</p>
            )}
          </div>
        )}
        {term && (
          <div className="px-4 py-3 rounded-lg border bg-zinc-50 border-zinc-200" data-testid="contract-enrich-termination">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle size={13} className="text-zinc-700" />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.contract.termination")}</p>
            </div>
            <p className="text-sm font-semibold">
              {term.for_cause_only ? t("enrichment.contract.for_cause_only") : t("enrichment.contract.for_convenience_allowed")}
              {term.termination_notice_days != null ? ` · ${t("enrichment.contract.day_notice", { count: term.termination_notice_days })}` : ""}
            </p>
            {term.termination_fee != null && (
              <p className="text-xs text-zinc-600 mt-1">{t("enrichment.contract.exit_fee")} {money(term.termination_fee)}</p>
            )}
          </div>
        )}
      </div>

      {/* Required insurance — feeds Contract↔Insurance gap analysis */}
      {reqIns.length > 0 && (
        <div className="mb-6" data-testid="contract-enrich-required-insurance">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.contract.required_insurance_label", { count: reqIns.length })}
          </p>
          <div className="space-y-1.5">
            {reqIns.map((r, i) => (
              <div
                key={i}
                data-testid={`contract-required-insurance-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-200 bg-white"
              >
                <span className="text-sm font-medium">{t(`enrichment.contract.coverage_type_options.${r.coverage_type}`, titleCase(r.coverage_type))}</span>
                <span className="font-mono text-xs text-zinc-700 tabular-nums flex items-center gap-2">
                  {r.minimum_amount != null && <span>{t("enrichment.contract.min_suffix", { amount: money(r.minimum_amount) })}</span>}
                  {r.additional_insured_required && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-semibold">{t("enrichment.contract.additional_insured_chip")}</span>
                  )}
                  {r.waiver_of_subrogation_required && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-semibold">{t("enrichment.contract.waiver_chip")}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SLAs */}
      {e.sla?.length > 0 && (
        <div className="mb-6" data-testid="contract-enrich-sla">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">{t("enrichment.contract.sla_label", { count: e.sla.length })}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {e.sla.map((s, i) => (
              <div key={i} className="px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200 text-sm" data-testid={`contract-sla-${i}`}>
                <span className="font-semibold">{s.metric}</span>
                <span className="text-zinc-600"> · {s.target}</span>
                {s.credit_pct != null && (
                  <span className="text-[10px] uppercase tracking-wider ml-2 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 font-semibold">
                    {t("enrichment.contract.credit_pct", { pct: s.credit_pct })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flag row */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {e.data_ownership && (
          <Flag tone={e.data_ownership === "customer" ? "emerald" : "amber"}>
            {t("enrichment.contract.data_label")} {t(`enrichment.contract.data_ownership_options.${e.data_ownership}`, titleCase(e.data_ownership))}
          </Flag>
        )}
        {e.ip_assignment && <Flag tone="zinc">{t("enrichment.contract.ip_label")} {t(`enrichment.contract.ip_assignment_options.${e.ip_assignment}`, titleCase(e.ip_assignment))}</Flag>}
        {e.exclusivity?.present && <Flag tone="amber">{t("enrichment.contract.exclusivity")}</Flag>}
        {e.non_compete?.present && (
          <Flag tone="rose">{t("enrichment.contract.non_compete", { months: e.non_compete.duration_months ?? "?" })}</Flag>
        )}
        {e.change_of_control_clause && <Flag tone="zinc">{t("enrichment.contract.change_of_control")}</Flag>}
        {e.dispute_resolution && (
          <Flag tone="zinc"><BadgePercent size={9} className="inline -mt-0.5" /> {t(`enrichment.contract.dispute_resolution_options.${e.dispute_resolution}`, titleCase(e.dispute_resolution))}</Flag>
        )}
        {e.governing_law && <Flag tone="zinc">{t("enrichment.contract.governing_law")} {e.governing_law}</Flag>}
      </div>

      {e.summary && (
        <p className="text-sm text-zinc-700 leading-relaxed mt-3" data-testid="contract-enrich-summary">
          {e.summary}
        </p>
      )}
    </section>
  );
}

function Tile({ label, value, accent, testid }) {
  const accents = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700" };
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">{label}</p>
      <p className={`font-display font-semibold text-zinc-950 tabular-nums ${accent ? accents[accent] : ""}`}>{value}</p>
    </div>
  );
}

function Flag({ tone, children }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}
